import { z } from 'zod';

const elementSchema = z.object({
  id: z.string().min(1), text: z.string().trim().min(1).max(2000),
  rowId: z.string().min(1).optional(),
});
const columnSchema = z.object({id:z.string().min(1),label:z.string().trim().min(1),elements:z.array(elementSchema)});
export const visibleCopyGroupSchema = z.object({
  id:z.string().min(1),
  type:z.enum(['headline','support','list','comparison','quote','steps','labels','cta','disclaimer']),
  label:z.string().optional(),
  elements:z.array(elementSchema),
  columns:z.array(columnSchema).min(2).optional(),
}).superRefine((group,ctx)=>{
  if(group.type==='comparison') {
    if(group.elements.length)ctx.addIssue({code:'custom',message:'Place comparison headings or introductory copy in a separate headline or support group'});
    if(!group.columns?.length) {ctx.addIssue({code:'custom',message:'Comparison requires columns'});return;}
    const first=group.columns[0].elements.map(e=>e.rowId);
    if(!first.length || first.some(id=>!id) || new Set(first).size!==first.length || group.columns.some(c=>JSON.stringify(c.elements.map(e=>e.rowId))!==JSON.stringify(first)))
      ctx.addIssue({code:'custom',message:'Comparison columns must have matching rows in the same order'});
  } else if(group.columns)ctx.addIssue({code:'custom',message:'Columns are only supported in comparison groups'});
  else if(!group.elements.length) ctx.addIssue({code:'custom',message:'Copy group requires visible text'});
});
export const visibleCopySchema = z.object({version:z.literal(1),groups:z.array(visibleCopyGroupSchema).min(1)}).superRefine((manifest,ctx)=>{
  const ids=manifest.groups.flatMap(g=>[g.id,...g.elements.map(e=>e.id),...(g.columns??[]).flatMap(c=>[c.id,...c.elements.map(e=>e.id)])]);
  if(new Set(ids).size!==ids.length)ctx.addIssue({code:'custom',message:'Copy group, column and element IDs must be unique'});
});
export type VisibleCopy = z.infer<typeof visibleCopySchema>;
export type VisibleCopyGroup = z.infer<typeof visibleCopyGroupSchema>;
export const sourceVisualContractSchema=z.object({
  composition:z.enum(['collage','split','grid','single-scene','typographic','other']),
  cameraView:z.enum(['overhead','eye-level','angled','mixed','not-applicable']),
  humanPresence:z.enum(['person-led','person-secondary','hands-only','none']).optional(),
  productPlacement:z.string().trim().min(5).max(600),
  textPlacement:z.string().trim().min(5).max(600),
  // Optional for saved v2 briefs; fresh source analysis must explicitly observe presence/absence.
  brandMark:z.object({present:z.boolean(),placement:z.string().trim().min(1).max(600).optional()}).optional(),
  groupTreatments:z.array(z.enum(['plain-text','button','offer-badge','panel'])),
});
export type SourceVisualContract=z.infer<typeof sourceVisualContractSchema>;
export const inspirationSchema=z.object({
  version:z.literal(1),winningAdId:z.number().int().positive(),imageUrl:z.string().url(),
  layout:z.string().min(1).max(3000),
  groups:z.array(z.object({type:z.enum(['headline','support','list','comparison','quote','steps','labels','cta','disclaimer']),count:z.number().int().positive().max(100)})),
  sourceHumanIdentityForbidden:z.literal(true),
  analysisVersion:z.union([z.literal(2),z.literal(3)]).optional(),visualContract:sourceVisualContractSchema.optional(),
});
export type Inspiration=z.infer<typeof inspirationSchema>;
/** Uploaded layout provenance never impersonates a numeric library record. */
const uploadedLayoutInspirationShape=inspirationSchema.omit({winningAdId:true}).extend({
  uploadedReferenceId:z.string().regex(/^uploaded-layout-[a-f0-9]{24}$/),
});
export const uploadedLayoutInspirationSchema=z.preprocess(value=>value&&typeof value==='object'&&'winningAdId' in value&&value.winningAdId!==undefined?null:value,uploadedLayoutInspirationShape);
export const layoutInspirationSchema=z.preprocess(value=>value&&typeof value==='object'&&'winningAdId' in value&&value.winningAdId!==undefined&&'uploadedReferenceId' in value&&value.uploadedReferenceId!==undefined?null:value,z.union([inspirationSchema,uploadedLayoutInspirationSchema]));
export type LayoutInspiration=z.infer<typeof layoutInspirationSchema>;
export function layoutSourceKey(source:LayoutInspiration):number|string{
 if('uploadedReferenceId' in source&&typeof source.uploadedReferenceId==='string')return source.uploadedReferenceId;
 if('winningAdId' in source&&typeof source.winningAdId==='number')return source.winningAdId;
 throw Error('A layout source needs its original provenance.');
}
export function layoutSourceProvenance(source:LayoutInspiration){
 if('uploadedReferenceId' in source&&source.uploadedReferenceId)return {sourceUploadedReferenceId:source.uploadedReferenceId,sourceImageUrl:source.imageUrl};
 if('winningAdId' in source&&source.winningAdId)return {sourceWinningAdId:source.winningAdId};
 throw Error('A layout source needs its original provenance.');
}
export const briefSourceKey=(source:{sourceWinningAdId?:unknown;sourceUploadedReferenceId?:unknown}|undefined)=>source?.sourceUploadedReferenceId??source?.sourceWinningAdId;

/** Text-only library provenance. This is never an image-generation reference. */
export const briefStructureSchema=z.object({
  version:z.literal(1),sourceWinningAdId:z.number().int().positive().optional(),
  sourceUploadedReferenceId:z.string().regex(/^uploaded-layout-[a-f0-9]{24}$/).optional(),sourceImageUrl:z.string().url().optional(),
  sourceStyle:z.string().nullable().optional(),sourceAngle:z.string().nullable().optional(),
  sourceLayout:z.string().min(1).max(3000),sourceGroups:inspirationSchema.shape.groups,
  sourceVisualContract:sourceVisualContractSchema.optional(),
  composition:sourceVisualContractSchema.shape.composition.optional(),
  cameraView:sourceVisualContractSchema.shape.cameraView.optional(),
  preserveSourceGeometry:z.boolean().optional(),
  groupTreatments:z.record(z.enum(['plain-text','button','offer-badge','panel'])).optional(),
  layout:z.string().trim().min(20).max(3000),rationale:z.string().trim().min(20).max(1000),
}).refine(value=>value.sourceUploadedReferenceId?!!value.sourceImageUrl&&value.sourceWinningAdId===undefined:!!value.sourceWinningAdId&&!value.sourceImageUrl,'Bind exactly one library or uploaded layout source.');
export type BriefStructure=z.infer<typeof briefStructureSchema>;
export function legacyVisibleCopy(image:{headlines?:string[];subheadlines?:string[];badges?:string[]}):VisibleCopy {
  const groups:VisibleCopyGroup[]=[];
  for(const [type,values] of [['headline',image.headlines],['support',image.subheadlines],['labels',image.badges]] as const) {
    if(values?.[0]?.trim())groups.push({id:`legacy-${type}`,type,elements:[{id:`legacy-${type}-text`,text:values[0]}]});
  }
  return {version:1,groups};
}
export function visibleCopyTexts(manifest:VisibleCopy):string[] {
  return manifest.groups.flatMap(g=>[...g.elements.map(e=>e.text),...(g.columns??[]).flatMap(c=>[c.label,...c.elements.map(e=>e.text)])]);
}
/** Only approved words enter this block. Source geometry, IDs and editor labels never do. */
export function compileVisibleCopy(manifest:VisibleCopy):string {
  const parsed=visibleCopySchema.parse(manifest);
  return parsed.groups.map(g=>g.columns
    ? g.columns.map(c=>[c.label,...c.elements.map(e=>e.text)].map(text=>JSON.stringify(text)).join('\n')).join('\n\n')
    : g.elements.map(e=>JSON.stringify(e.text)).join('\n')).join('\n\n');
}
/** Editor drafts may contain empty text until validated on save. */
export function addComparisonRow(group:VisibleCopyGroup,rowId:string):VisibleCopyGroup {
  return {...group,columns:group.columns?.map(c=>({...c,elements:[...c.elements,{id:`${c.id}-${rowId}`,rowId,text:''}]}))};
}
export function removeComparisonRow(group:VisibleCopyGroup,rowId:string):VisibleCopyGroup {
  return {...group,columns:group.columns?.map(c=>({...c,elements:c.elements.filter(e=>e.rowId!==rowId)}))};
}

export function inspirationStructureMatches(manifest:VisibleCopy,source:Inspiration):boolean {
  const actual=new Map<string,number>();
  const expected=new Map<string,number>();
  for(const group of manifest.groups)actual.set(group.type,(actual.get(group.type)??0)+(group.type==='comparison'?group.columns?.[0]?.elements.length??0:group.elements.length));
  for(const group of source.groups)expected.set(group.type,(expected.get(group.type)??0)+group.count);
  return Array.from(expected).every(([type,count])=>actual.get(type)===count);
}

/** Optional on saved briefs. Describes only subjects already present in the approved scene. */
export const humanDetailSchema=z.object({
  visibility:z.enum(['person','hands-only']),
  depiction:z.enum(['photographic','illustrated']),
  subjects:z.array(z.object({
    age:z.string().trim().min(1).max(200).optional(),
    features:z.string().trim().min(1).max(500).optional(),
    hair:z.string().trim().min(1).max(300).optional(),
    skin:z.string().trim().min(1).max(500),
    expression:z.string().trim().min(1).max(300).optional(),
    clothing:z.string().trim().min(1).max(500).optional(),
    action:z.string().trim().min(1).max(600),
  })).min(1).max(12),
  cameraContext:z.string().trim().min(1).max(600),
});
export type HumanDetail=z.infer<typeof humanDetailSchema>;
export const visualPlanSchema=z.object({
  productVisibility:z.enum(['none','secondary','hero']),
  background:z.string().trim().min(1).max(300),
  people:z.enum(['none','fresh-fictional']),
  humanDetail:humanDetailSchema.optional(),
});
export type VisualPlan=z.infer<typeof visualPlanSchema>;
export function assertVisualPlan(plan:VisualPlan|undefined,description:string):void {
 if(!plan)return;
 const forbidsProduct=/\b(?:no|without)\s+(?:visible\s+)?(?:product|packaging|packshot|pouch|bottle)\b|\b(?:product|packaging|packshot)\s+(?:must\s+)?(?:not|never)\s+(?:appear|be\s+(?:shown|visible))/i.test(description);
 if(forbidsProduct && plan.productVisibility!=='none')throw new Error('Brief contradicts its product visibility. Edit the scene or visibility before generation.');
}
